import { Quote } from '@/model/quotesModel';
import { extractInvestmentMaxs, extractInvestmentMins } from '@/utils/investmentBounds';
import { useMemo } from 'react';
import type { SliderBounds } from '@/features/shop-insurance';

const DEFAULT_BOUNDS: SliderBounds = {
  min: 100000,
  max: 200000,
  midPoint: 150000,
  defaultValue: 100000,
  isCalculatedFromQuotes: false,
};

export const useSliderBounds = (
  quotes: Quote[] | undefined,
  selectedTab: ShopInsuranceTab,
): SliderBounds => {
  return useMemo(() => {
    if (!quotes || quotes.length === 0) {
      return DEFAULT_BOUNDS;
    }

    try {
      const filteredQuotes = quotes.filter(quote => {
        if (selectedTab === 'joint') {
          return quote.policyType === 'Joint Life';
        }
        // The Couples Discount tab was removed — its quotes are folded into the Single tab.
        return (
          quote.policyType === 'Single Life' ||
          quote.policyType === 'Standard' ||
          quote.policyType === 'Couples Discount'
        );
      });

      if (filteredQuotes.length === 0) {
        return DEFAULT_BOUNDS;
      }

      const investmentMins = extractInvestmentMins(filteredQuotes);
      const investmentMaxs = extractInvestmentMaxs(filteredQuotes);

      if (investmentMins.length === 0 || investmentMaxs.length === 0) {
        return DEFAULT_BOUNDS;
      }

      const finalSliderMin = Math.min(...investmentMins);
      const finalSliderMax = Math.max(...investmentMaxs);

      if (finalSliderMax <= finalSliderMin) {
        return DEFAULT_BOUNDS;
      }

      const midPoint = Math.floor((finalSliderMin + finalSliderMax) / 2);

      const defaultValue =
        filteredQuotes.length > 0 && filteredQuotes[0].premiumAmount > 0
          ? Math.max(finalSliderMin, Math.min(finalSliderMax, filteredQuotes[0].premiumAmount))
          : midPoint;

      return {
        min: finalSliderMin,
        max: finalSliderMax,
        midPoint,
        defaultValue,
        isCalculatedFromQuotes: true,
      };
    } catch (error) {
      console.error('Error calculating slider bounds:', error);
      return DEFAULT_BOUNDS;
    }
  }, [quotes, selectedTab]);
};
